import { memo, useEffect, useRef, useState } from 'react';

import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import VeVideo from '@wconcept/wconcept-ui-renewal/components/atoms/VeVideo';
import PrsnlTag from '@wconcept/wconcept-ui-renewal/components/molecules/_tag/PrsnlTag';
import PlayIcon from '@wconcept/wconcept-ui-renewal/components/molecules/_icon/PlayIcon';
import {
  IconAlarm03,
  IconArrowRight11,
  IconCheck03,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { getResizeImageUrl } from '@wconcept/wconcept-common/utils/domainUtil';
import { IPlayAuthTokenConfig } from '@byteplus/veplayer';

import * as S from './style';

interface IProps {
  videoSrc?: string;
  videoPoster?: string;
  vid?: string;
  token?: IPlayAuthTokenConfig;
  rs?: string;
  liveStatus: 'onair' | 'ready' | 'notiDone' | 'ended' | string;
  liveDate?: string;
  liveTitle?: string;
  liveDesc?: string;
  isDesktop?: boolean;
  getNewToken?: () => Promise<IPlayAuthTokenConfig>;
  onClick?: () => void;
}

const getButtonByStatus = (liveStatus: string, onClick?: () => void) => {
  switch (liveStatus) {
    case 'ready':
      return (
        <Button onClick={onClick} className="btn-alarm">
          <IconAlarm03 />
          알림신청
        </Button>
      );
    case 'notiDone':
      return (
        <Button onClick={onClick} className="btn-alarm active">
          <IconCheck03 />
          알림신청 완료
        </Button>
      );
    case 'onair':
      return (
        <Button onClick={onClick} className="btn-live">
          라이브 보기
          <IconArrowRight11 />
        </Button>
      );
    case 'ended':
      return (
        <Button onClick={onClick} className="btn-replay">
          라이브 다시보기
          <IconArrowRight11 />
        </Button>
      );
    default:
      return null;
  }
};

const WLiveItem = ({
  videoSrc,
  videoPoster,
  vid,
  token,
  rs,
  liveStatus,
  liveDate,
  liveTitle,
  liveDesc,
  isDesktop = false,
  getNewToken,
  onClick,
}: IProps) => {
  const [isActive, setIsActive] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsActive(entry.isIntersecting);
        });
      },
      { threshold: 0.7 }
    );

    const currentItem = itemRef.current;
    if (currentItem) {
      observer.observe(currentItem);
    }

    return () => {
      if (currentItem) {
        observer.unobserve(currentItem);
      }
    };
  }, []);

  // 데스크탑/모바일 공통 정보
  const renderVideoInfo = () => {
    if (isDesktop) {
      return (
        <div className="video-info">
          <div className="video-status">
            <PrsnlTag
              liveType={liveStatus === 'notiDone' ? 'ready' : liveStatus}
            />
            {(liveStatus === 'notiDone' || liveStatus === 'ready') && (
              <Text
                className="live-date"
                weight={600}
                size={20}
                lineHeight={24}
                color="#ff4600"
                block
              >
                {liveDate}
              </Text>
            )}
          </div>
          <Text
            className="title"
            weight={600}
            size={38}
            color="#000"
            block
            ellipsis={1}
          >
            {liveTitle}
          </Text>
          <Text
            className="desc"
            weight={500}
            size={26}
            color="#000"
            block
            ellipsis={1}
          >
            {liveDesc}
          </Text>
          {getButtonByStatus(liveStatus, onClick)}
        </div>
      );
    }
    // 모바일
    return (
      <div className="video-info">
        <div className="video-info-top">
          <PrsnlTag
            liveType={liveStatus === 'notiDone' ? 'ready' : liveStatus}
          />
          {liveStatus === 'onair' && <PlayIcon />}
        </div>
        <div className="video-info-bottom">
          {(liveStatus === 'notiDone' || liveStatus === 'ready') && (
            <Text
              className="live-date"
              weight={600}
              size={16}
              lineHeight={22}
              color="#fff"
              block
            >
              {liveDate}
            </Text>
          )}
          <Text
            className="title"
            weight={600}
            size={22}
            lineHeight="130%"
            color="#fff"
            block
            ellipsis={1}
          >
            {liveTitle}
          </Text>
          <Text
            className="desc"
            weight={400}
            size={16}
            lineHeight="130%"
            color="#fff"
            block
            ellipsis={1}
          >
            {liveDesc}
          </Text>
          {getButtonByStatus(liveStatus, onClick)}
        </div>
      </div>
    );
  };

  return (
    <S.WLiveItem className="item" ref={itemRef}>
      {isDesktop && (
        <Img src={getResizeImageUrl(videoPoster, rs)} className="bg-img" />
      )}

      <div className="ly-pc">
        <div className="video-wrap">
          {liveStatus === 'onair' ? (
            <>
              <VeVideo
                active={isActive}
                src={videoSrc}
                poster={videoPoster}
                vid={vid}
                token={token}
                muted
                loop
                autoplay
                getNewToken={getNewToken}
              />
              {isDesktop && <PlayIcon size="lg" />}
            </>
          ) : (
            <Img
              src={getResizeImageUrl(videoPoster, rs)}
              className="ready-img"
            />
          )}
          {!isDesktop && renderVideoInfo()}
        </div>

        {isDesktop && renderVideoInfo()}
      </div>
    </S.WLiveItem>
  );
};

export default memo(WLiveItem);
